// Application-wide constants

// UI/UX Constants
export const UI_CONSTANTS = {
  MOBILE_BREAKPOINT: 768,
  DROPDOWN_THRESHOLD_HEIGHT: 200,
  FAKE_NOTICE_DURATION: 3000,
  HOTKEY_BUTTON_ANIMATION_DURATION: 100,
} as const;

export const FEATURE_FLAG_CONSTANTS = {
  DURATION: 60 * 60 * 1000, // 1 hour in milliseconds
  COOKIE_PREFIX: 'ff_',
} as const;

export const FORM_VALIDATION_CONSTANTS = {
  MIN_BIRTH_YEAR_OFFSET: 100, // Allow users up to 100 years old
  MAX_BIRTH_YEAR_OFFSET: 18, // Users must be at least 17 years old
} as const;

export const TEST_CONSTANTS = {
  // Web test version history (maps to web_test_version sent to backend):
  //
  // Legacy (non-adaptive) frontend versions:
  //   v4       Jun 2025        First sentence-based UI
  //   v5       Jun-Aug 2025    UX variants
  //   v6       Jul-Sep 2025    Modern interface
  //   v7       Sep-Dec 2025    Refined interface
  //
  // Adaptive frontend versions (this constant):
  //   8        Dec 2025        First adaptive (IRT) frontend, sentence mode
  //   9        Jan 2026        Sentence-based with calibration phase
  //   10       Jan 2026        Word-based hybrid test with calibration
  //   11       Mar 2026        Remove fake-word warnings, add know-clarification screens
  //   12       Jun 2026        Batched word-based test: server returns N items at once
  //                            (batch_words mode), UI shows them together with checkboxes.
  //                            Batch size is returned via `client_buffer_size` in the test response.
  //   13       Sep 2026        Page-based vocab test: the backend owns the page sequence
  //                            (`GET/POST /pages/`) and interleaves headword-translation
  //                            pages (type the translation of a word) between the
  //                            checkbox batches. No Back button, no know-clarification
  //                            screens, no page index in the URL. A stored v12 vocab
  //                            test is wiped by the version check below and a v13 one is
  //                            created.
  ADAPTIVE_VERSION: 13,
  // Hard limit on a typed headword translation. Mirrors the backend's
  // HEADWORD_TRANSLATION_MAX_LENGTH (wire schema and DB column).
  HEADWORD_TRANSLATION_MAX_LENGTH: 60,
} as const;

export const COOKIE_CONSTANTS = {
  REFERRER_COOKIE_NAME: 'gll2vocabreferrer',
  REFERRER_COOKIE_EXPIRY: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString(), // 30 days in UTC format
} as const;

export const DECK_STORAGE_COOKIE = {
  DECK_STORAGE_NAME: 'gll2vocabdeck',
};

export const LOCAL_STORAGE_CONSTANTS = {
  VOCAB_TEST_STORAGE_KEY: 'vocabTest',
  COMPLETED_TEST_STORAGE_KEY: 'completedVocabTests',
} as const;

export const ANIMATION_VARIANTS = {
  forward: {
    initial: { opacity: 0, x: 150 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: -150 },
  },
  backward: {
    initial: { opacity: 0, x: -150 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: 150 },
  },
  slideFromRight: {
    initial: { opacity: 0, x: 100 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: 100 },
  },
  popover: {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  },
} as const;

export const PAGE_TRANSITION = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.3,
} as const;
